import type { BrightnessCorrectionState } from './collection.brightnessCorrection';
import type { CropParameterState } from './collection.cropParameter';
import type { DrawableState } from './collection.drawable';
import type { CollectionFieldData } from './collection.field';
import type { ImageVisibilityState } from './collection.imageVisibility';
import type { ParseDateJSON, ParseHistoryJSONObject, ParseJSONObject } from './parse';

export interface CollectionMeasurements {
	state: DrawableState[];
	/**
	 * @deprecated We have no plans to add other types of measurements
	 */
	type?: 'truesize';
}

export interface CollectionMeasurementsJson extends CollectionMeasurements {
	/** CaptureId of the measurements @see Pointer<Capture.objectId> */
	captureId: string;
}

export interface CollectionBrightnessCorrections {
	state: BrightnessCorrectionState[];
	type: string;
}

export interface CollectionBrightnessCorrectionsJson extends CollectionBrightnessCorrections {
	/** CaptureId of the brightness corrections @see Pointer<Capture.objectId> */
	captureId: string;
}

export interface CollectionImageVisibility {
	state: ImageVisibilityState;
	type: string;
}

export interface CollectionImageVisibilityJson extends CollectionImageVisibility {
	/** CaptureId of the image visibilities @see Pointer<Capture.objectId> */
	captureId: string;
}

export interface CollectionCropParameters {
	state: CropParameterState[];
	type: string;
}

export interface CollectionCropParametersJson extends CollectionCropParameters {
	/** CaptureId of the crop parameter @see Pointer<Capture.objectId> */
	captureId: string;
}

/** Reference from an "Existing" collection to one of its pole variants. */
export interface CollectionVariantRef {
	/** Variant type of the referenced collection variant (e.g. 'proposed'). */
	variantType: string;
	/** Human-readable variant type name supplied by the publisher (e.g. 'Proposed'). */
	variantTypeName?: string;
	/** uuid of the collection variant. */
	variantUuid: string;
}

/**
 * A collection is a "collection variant" (a variant of a collection) when it has a `variantType`.
 * Variants live in a non-"Existing" layer and are read-only in Office. Shared discriminator used to
 * keep variants out of the map, public read API, search, and events. `variantForUuid` is optional
 * provenance (the "Existing" pole a variant derives from, when one exists); it is absent for poles
 * created in Verify with no Existing base, so it is no longer the discriminator.
 */
export function isCollectionVariant(collection: { variantType?: string }): boolean {
	return collection.variantType !== undefined;
}

export interface CollectionJSONObject extends ParseJSONObject {
	uuid: string;
	data: {
		data: CollectionFieldData;

		/**
		 * @deprecated Same as rootFormId
		 */
		recordeduuid?: string;

		/**
		 * @deprecated Same as formuuid
		 */
		uuid?: string;

		/**
		 * @deprecated Same as formversion
		 */
		version?: string;

		/**
		 * @deprecated Does not appear to be used
		 */
		createdat?: number;
	};
	formuuid: string;
	formversion: string;
	jobuuid: string;
	measurements?: { [captureId: string]: CollectionMeasurementsJson };
	brightnessCorrections?: { [uuid: string]: CollectionBrightnessCorrectionsJson };
	cropParameters?: { [uuid: string]: CollectionCropParametersJson };
	imageVisibilities?: { [uuid: string]: CollectionImageVisibilityJson };
	revision: string;
	/** User id that this collection is assigned to */
	assignedTo?: string;
	collectedBy?: string;
	collectedAt?: ParseDateJSON | null;
	collectedWith?: string;
	updatedBy?: string;
	/** Variant type (e.g. 'proposed') when this collection is a pole variant. Undefined when not a variant. */
	variantType?: string;
	/** Human-readable variant type name (e.g. 'Proposed') when this collection is a pole variant. */
	variantTypeName?: string;
	/**
	 * uuid of the "Existing" collection this variant derives from, when one exists (optional provenance).
	 * Absent for poles created in Verify with no Existing base. Not the variant discriminator — see
	 * {@link isCollectionVariant}, which keys on `variantType`.
	 */
	variantForUuid?: string;
	/** Variants of this collection (present on the "Existing" parent). Undefined/omitted when none. */
	variants?: CollectionVariantRef[];
}

export interface CollectionHistoryJSONObject extends CollectionJSONObject, ParseHistoryJSONObject {}
